import { makeStyles, TableCell, TableRow, Theme, IconButton } from "@material-ui/core";
import { Launch } from "@material-ui/icons";
import clsx from "clsx";

import { TableItem } from "~components/Table/index";
import { resolvePath } from "~components/Table/helpers";
import { BaseRowProps } from "~components/TableRows/Base";

export type TransactionTableItem = TableItem & {
  id: string;
  transferType: string | null;
  currency: {
    id: string;
    name: string;
    decimal: number;
    active: boolean;
  };
  amount: number;
  type: string;
  bank: {
    providedBy: string[];
    branchName: string;
    officialName: string;
    country: { id: string; name: string };
    shortBranchName: string | null;
    localName: string | null;
    id: string;
    active: boolean;
    createdAt: string;
    logo: string;
    icon: string;
  } | null;
  uniqueReference: string;
  status: string;
  active: boolean;
  createdAt: string;
};

export type TransactionRowProps<T extends TransactionTableItem> = {} & BaseRowProps<T>;

const useStyles = makeStyles((theme: Theme) => ({
  tableRow: {
    transition:
      "box-shadow ease-in-out, .1s backgroundColor ease-in-out .18s, width ease-in-out .1s",

    "&:nth-of-type(odd)": {
      backgroundColor: "#fafbfc",
    },

    "&:nth-of-type(even)": {
      backgroundColor: "white",
    },

    "&:hover": {
      cursor: "pointer",
      backgroundColor: theme.palette.background.default,

      "& td": {
        "&:last-child": {
          border: 0,

          "& button": {
            opacity: 1,
          },
        },
      },
    },
  },
  activeRow: {
    cursor: "pointer",
    backgroundColor: "rgba(26, 99, 171, 0.05)!important",
    boxShadow: `inset 5px 0 0 0 ${theme.palette.primary.main}`,

    "& td": {
      "&:last-child": {
        border: 0,

        "& button": {
          opacity: 1,
        },
      },
    },
  },
  hiddenTableCell: {
    fontSize: 14,
    lineHeight: "16px",
    padding: `${theme.spacing(1)}px ${theme.spacing(1.75)}px !important`,
    border: 0,

    "& button": {
      opacity: 0,
    },
  },
  tableCell: {
    fontSize: 14,
    fontWeight: "normal",
    lineHeight: "16px",
    whiteSpace: "nowrap",
    color: theme.palette.dark.main,

    padding: `${theme.spacing(2)}px ${theme.spacing(1.75)}px`,

    border: 0,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    textTransform: "capitalize",
  },
  status: {
    width: 6,
    height: 6,
    marginRight: 8,

    borderRadius: 6,
  },
  statusNew: {
    backgroundColor: theme.palette.warning.main,
  },
  statusCompleted: {
    backgroundColor: theme.palette.success.main,
  },
  iconRoot: {
    padding: 0,
  },
  launch: { fontSize: 16 },
}));

export const TransactionRow = <T extends TransactionTableItem>({
  row,
  activeRow,
  keys,
  isCondensed,
  onRowClick,
  onActionClick,
  renderValue,
}: TransactionRowProps<T>) => {
  const classes = useStyles();
  return (
    <TableRow
      key={row.id}
      onClick={() => onRowClick(row)}
      className={clsx(classes.tableRow, {
        [classes.activeRow]: activeRow && activeRow.id === row.id,
      })}
    >
      {keys.map(({ key, isNumeric }, index) => (
        <TableCell
          className={classes.tableCell}
          component={"td"}
          scope={index === 0 ? "row" : ""}
          align={isNumeric ? "right" : undefined}
        >
          {typeof renderValue === "function" ? renderValue(key, row) : resolvePath(row, key)}
        </TableCell>
      ))}
      {!isCondensed && (
        <TableCell className={classes.hiddenTableCell}>
          <IconButton
            color="primary"
            size="small"
            classes={{ root: classes.iconRoot }}
            onClick={(e) => {
              e.stopPropagation();

              if (typeof onActionClick === "function") {
                onActionClick(row);
              }
            }}
          >
            <Launch className={classes.launch} />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
};
